import * as React from "react";

import { assertNever } from "@impero-com/common/utils/obj";

import {
    SelectInput,
    SelectInputProps,
    SelectInputOption,
    FORM_SELECT_UNDEFINED_VALUE,
} from "components/form/legacyFields/selectInput";
import * as CT from "org/risks/content/types";
import * as R from "org/risks/types";

import { mapForm, simpleMappers, WrappedFormUtils } from "../../../utils/form";
import * as U from "../content/utils";

import * as T from "./types";

type DropdownMetadataFieldProps<ROOT> = T.SharedProperties<
    string,
    ROOT,
    CT.DropdownInstance
> & {
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
};

export default class DropdownMetadataField<ROOT> extends React.PureComponent<
    DropdownMetadataFieldProps<ROOT>
> {
    render = () => {
        const { id, label, instance } = this.props;
        const { fieldType } = instance;
        const baseProps = {
            id,
            label,
            // The first option's value is undefined, label is a non-breakable space (0x00A0) because antd does not display regular spaces correctly.
            // The rest just destructures from the props
            options: [
                { value: undefined, label: "\xa0" },
                ...fieldType.options.map(mapOptionValueToSelectInputOption),
            ],
        };
        let props: SelectInputProps<string, ROOT>;
        switch (this.props.type) {
            case "CREATE":
                props = {
                    ...baseProps,
                    type: this.props.type,
                    initialValue: undefined,
                    form: mapInstanceFormToOptionIdForm(
                        id,
                        this.props.form,
                        instance
                    ),
                    getPopupContainer: this.props.getPopupContainer,
                };
                break;
            case "EDIT":
                props = {
                    ...baseProps,
                    type: this.props.type,
                    value: this.props.instance.value?.id,
                    onChange: this.handleOnChange,
                    getPopupContainer: this.props.getPopupContainer,
                    validationError: this.props.validationError,
                };
                break;
            case "READ_ONLY":
                props = {
                    ...baseProps,
                    type: this.props.type,
                    value: this.props.instance.value?.id,
                };
                break;
            default:
                return assertNever(this.props);
        }
        return <SelectInput {...props} />;
    };

    private handleOnChange = (value: string | undefined) => {
        const { instance } = this.props;
        if (this.props.type === "EDIT") {
            return this.props.onChangeAsync(
                Promise.resolve(value),
                (_prev, val) =>
                    U.extractMetadataInstanceValue(
                        optionIdToInstance(val, instance)
                    )
            );
        }
        return Promise.reject(
            new Error("Cannot change value in non-edit mode")
        );
    };
}

function mapOptionValueToSelectInputOption(
    optionValue: R.OptionValue
): SelectInputOption {
    return {
        value: optionValue.id,
        label: optionValue.value,
    };
}

function instanceToOptionId(instance: CT.MetadataInstance): string | undefined {
    if (instance.type !== "DropdownInstance") {
        throw new Error(
            "Expected dropdown group instance, got " + instance.type
        );
    }
    const { value } = instance;
    return value?.id;
}

function optionIdToInstance(
    optionId: string | undefined,
    instance: CT.MetadataInstance
): CT.MetadataInstance {
    if (instance.type !== "DropdownInstance") {
        throw new Error(
            "Expected dropdown group instance, got " + instance.type
        );
    }
    const { options } = instance.fieldType;
    let newValue: R.OptionValue | undefined;
    if (optionId !== undefined && optionId !== FORM_SELECT_UNDEFINED_VALUE) {
        newValue = options.find((opt) => opt.id === optionId);
        if (newValue === undefined) {
            throw new Error("Unknown option id " + optionId);
        }
    } else {
        newValue = undefined;
    }

    return {
        ...instance,
        value: newValue,
    };
}

function mapInstanceFormToOptionIdForm<ROOT>(
    id: string,
    instanceForm: WrappedFormUtils<
        {
            [x: string]: CT.MetadataInstance;
        },
        ROOT
    >,
    instance: CT.MetadataInstance
): WrappedFormUtils<
    {
        [x: string]: string | undefined;
    },
    ROOT
> {
    const mapper = simpleMappers<
        string,
        CT.MetadataInstance,
        string | undefined,
        ROOT
    >(id, instanceToOptionId, (optionId) =>
        optionIdToInstance(optionId, instance)
    );
    return mapForm<Record<string, string | undefined>>()(
        instanceForm,
        mapper()
    );
}
